import React, { useEffect, useState } from 'react';
import type { FlatMaterialRow } from './commerceMaterialsModel.ts';
import ModalOverlay from '../../../UI/ModalOverlay/ModalOverlay.tsx';
import { parseDecimalField } from './commerceMaterialsCatalogPick.ts';
import styles from './CommerceMaterialsCatalog.module.scss';

export interface CommerceMaterialsCatalogEditModalProps {
  isOpen: boolean;
  row: FlatMaterialRow | null;
  itemId: number | null;
  flatRows: FlatMaterialRow[];
  disableActions?: boolean;
  onClose: () => void;
  onSave: (
    itemId: number,
    patch: {
      price: number | null;
      unquoted: boolean;
      valueAddedTax: boolean;
      promotion: string;
      promotionDiscount: number | null;
    }
  ) => Promise<void>;
}

export const CommerceMaterialsCatalogEditModal: React.FC<CommerceMaterialsCatalogEditModalProps> = ({
  isOpen,
  row,
  itemId,
  flatRows,
  disableActions = false,
  onClose,
  onSave,
}) => {
  const [editPrice, setEditPrice] = useState('');
  const [editPromotion, setEditPromotion] = useState('');
  const [editPromotionDiscount, setEditPromotionDiscount] = useState('');
  const [editUnquoted, setEditUnquoted] = useState(false);
  const [editVat, setEditVat] = useState(true);
  const [rowError, setRowError] = useState('');
  const [saving, setSaving] = useState(false);

  const showPublicNeedsValidPrice =
    !editUnquoted &&
    (editPrice.trim().length === 0 || parseDecimalField(editPrice) === null)

  useEffect(() => {
    if (!isOpen || !row || itemId == null) {
      setRowError('');
      return;
    }
    const mc = row.material.commerceRow;
    if (!mc) return;
    setRowError('');
    setEditPrice(
      mc.price == null || !Number.isFinite(mc.price)
        ? ''
        : new Intl.NumberFormat('es-AR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
            useGrouping: false,
          })
            .format(mc.price)
            .replace(',', '.')
    );
    setEditPromotion(mc.promotion ?? '');
    setEditPromotionDiscount(
      mc.promotion_discount == null || !Number.isFinite(mc.promotion_discount)
        ? ''
        : String(mc.promotion_discount)
    );
    setEditUnquoted(Boolean(mc.unquoted));
    setEditVat(mc.value_added_tax !== false);
  }, [isOpen, row, itemId]);

  useEffect(() => {
    if (!isOpen || !row || itemId == null) return;
    const key = `${row.categoryId}-${row.material.id}`;
    const stillThere = flatRows.some(
      (r) => `${r.categoryId}-${r.material.id}` === key && r.material.commerceRow?.itemId === itemId
    );
    if (!stillThere) {
      onClose();
    }
  }, [flatRows, isOpen, row, itemId, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !disableActions && !saving) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disableActions, isOpen, onClose, saving]);

  if (!isOpen || !row || itemId == null) {
    return null;
  }

  const busy = disableActions || saving

  return (
    <ModalOverlay isOpen onClose={busy ? undefined : onClose} zIndex={80} preventClose={busy}>
      <div
        className={`${styles.modalPanel} ${busy ? styles.modalPanelSaving : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cmc-edit-title"
        aria-busy={busy}
        onClick={(e) => e.stopPropagation()}
      >
        {busy ? (
          <div className={styles.modalBusyOverlay} aria-hidden>
            <svg className={styles.addModalSaveSpinnerSvg} viewBox="0 0 40 40">
              <circle className={styles.addModalSaveSpinnerTrack} cx="20" cy="20" r="16" />
              <circle className={styles.addModalSaveSpinnerArc} cx="20" cy="20" r="16" />
            </svg>
            <p className={styles.modalBusyText}>Guardando…</p>
          </div>
        ) : null}
        <div className={styles.modalHeader}>
          <h2 id="cmc-edit-title" className={styles.modalTitle}>
            Editar material
          </h2>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            disabled={busy}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <p className={styles.modalLead}>
          <strong>{row.material.name}</strong>
          <span className={styles.modalMeta}> · {row.categoryName}</span>
        </p>
        <div className={styles.modalBody}>
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="cmc-modal-price">
              Importe
            </label>
            <input
              id="cmc-modal-price"
              className={styles.modalInput}
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              disabled={disableActions}
            />
          </div>
          <label className={styles.modalCheck}>
            <input
              type="checkbox"
              checked={!editUnquoted}
              onChange={(e) => setEditUnquoted(!e.target.checked)}
              disabled={disableActions}
            />
            Mostrar precio al público
          </label>
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="cmc-modal-promo">
              Promoción
            </label>
            <input
              id="cmc-modal-promo"
              className={styles.modalInput}
              value={editPromotion}
              onChange={(e) => setEditPromotion(e.target.value)}
              disabled={disableActions}
            />
          </div>
          <div className={styles.modalField}>
            <label className={styles.modalLabel} htmlFor="cmc-modal-disc">
              Descuento (%)
            </label>
            <input
              id="cmc-modal-disc"
              className={styles.modalInput}
              value={editPromotionDiscount}
              onChange={(e) => setEditPromotionDiscount(e.target.value)}
              disabled={disableActions}
            />
          </div>
          {!editUnquoted ? (
            <label className={styles.modalCheck}>
              <input
                type="checkbox"
                checked={editVat}
                onChange={(e) => setEditVat(e.target.checked)}
                disabled={disableActions}
              />
              IVA incluido
            </label>
          ) : null}
          {rowError ? <p className={styles.modalError}>{rowError}</p> : null}
        </div>
        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.modalBtnSecondary}
            onClick={onClose}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.modalBtnPrimary}
            disabled={busy || showPublicNeedsValidPrice}
            onClick={async () => {
              const priceValue = parseDecimalField(editPrice);
              if (editPrice.trim().length > 0 && priceValue == null) {
                setRowError('Precio inválido.');
                return;
              }
              const discountValue = parseDecimalField(editPromotionDiscount);
              if (
                editPromotionDiscount.trim().length > 0 &&
                (discountValue == null || discountValue < 0 || discountValue > 100)
              ) {
                setRowError('El descuento debe estar entre 0 y 100.');
                return;
              }
              setRowError('');
              setSaving(true);
              try {
                await onSave(itemId, {
                  price: priceValue,
                  unquoted: editUnquoted,
                  valueAddedTax: editVat,
                  promotion: editPromotion,
                  promotionDiscount: discountValue,
                });
                onClose();
              } catch {
              } finally {
                setSaving(false);
              }
            }}
          >
            Guardar
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
};
